import { useEffect, useState } from 'react';
import {
  ExternalLink,
  Search,
  UsersRound,
} from 'lucide-react';
import { getDiagnosticLeads } from '../../api/diagnosticsApi';
import { getApiErrorMessage } from '../../api/apiClient';
import {
  DiagnosticLead,
  DiagnosticLeadPagedResult,
} from '../../types/diagnostics';
import Spinner from '../../components/Spinner';

const PAGE_SIZE = 20;

const DIAGNOSTIC_TYPES = [
  'Website',
  'Marketing',
  'Business',
  'Talent',
] as const;

const TYPE_CLASSES: Record<string, string> = {
  Website: 'bg-blue-50 text-blue-700',
  Marketing: 'bg-purple-50 text-purple-700',
  Business: 'bg-amber-50 text-amber-700',
  Talent: 'bg-emerald-50 text-emerald-700',
};

export default function DiagnosticLeadsPage() {
  const [data, setData] = useState<DiagnosticLeadPagedResult | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [diagnosticType, setDiagnosticType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadLeads = async () => {
      setLoading(true);
      setError('');

      try {
        const result = await getDiagnosticLeads({
          page: currentPage,
          pageSize: PAGE_SIZE,
          search: search || undefined,
          diagnosticType: diagnosticType || undefined,
        });

        setData(result);
      } catch (err) {
        setError(
          getApiErrorMessage(
            err,
            'Unable to load diagnostic leads.'
          )
        );
      } finally {
        setLoading(false);
      }
    };

    loadLeads();
  }, [currentPage, search, diagnosticType]);

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const applySearch = () => {
    setCurrentPage(1);
    setSearch(searchInput.trim());
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
    setDiagnosticType('');
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen p-6 pt-10 md:p-8 lg:p-10">
      <header className="mb-8 mt-5 text-left">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-800">
          <UsersRound
            size={24}
            className="text-strivre-burgundy"
          />
          Diagnostic Leads
        </h1>

        <p className="mt-1 text-sm text-slate-500">
          View people who submitted their information through Strivre diagnostic assessments.
        </p>
      </header>

      {/* Filters */}
      <div className="mb-6 rounded-2xl border border-strivre-lightnavy bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
          <div className="flex-1 text-left">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Search
            </label>

            <div className="relative">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                type="text"
                value={searchInput}
                onChange={(event) =>
                  setSearchInput(event.target.value)
                }
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    applySearch();
                  }
                }}
                placeholder="Name, email, company or website..."
                className="input-base w-full pl-10"
              />
            </div>
          </div>

          <div className="w-full text-left lg:w-56">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Diagnostic Type
            </label>

            <select
              value={diagnosticType}
              className="input-base w-full"
              onChange={(event) => {
                setDiagnosticType(event.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">
                All Diagnostics
              </option>

              {DIAGNOSTIC_TYPES.map((type) => (
                <option
                  key={type}
                  value={type}
                >
                  {type}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={applySearch}
            className="btn-primary btn-md inline-flex items-center justify-center gap-2"
          >
            <Search size={16} />
            Search
          </button>

          {(search || diagnosticType) && (
            <button
              type="button"
              onClick={clearSearch}
              className="btn-secondary btn-md"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-strivre-lightnavy bg-white shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center p-16">
            <Spinner size="h-6 w-6" />

            <span className="ml-3 text-sm text-slate-500">
              Loading diagnostic leads...
            </span>
          </div>
        ) : !data?.items.length ? (
          <div className="p-16 text-center">
            <UsersRound
              size={40}
              className="mx-auto mb-3 text-strivre-lightnavy"
            />

            <p className="font-semibold text-slate-600">
              No diagnostic leads found
            </p>

            <p className="mt-1 text-sm text-slate-400">
              Captured diagnostic leads will appear here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] text-left text-sm">
              <thead className="border-b border-strivre-lightnavy bg-slate-50">
                <tr>
                  {[
                    'Name',
                    'Email',
                    'Company',
                    'Website',
                    'Type',
                    'Submitted',
                    // 'Email',
                    'Report',
                  ].map((heading) => (
                    <th
                      key={heading}
                      className="px-5 py-4 text-xs font-bold uppercase tracking-wide text-slate-500"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {data.items.map((lead) => (
                  <DiagnosticLeadRow
                    key={lead.id}
                    lead={lead}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && total > 0 && (
        <footer className="mt-4 flex flex-col gap-3 rounded-2xl border border-strivre-lightnavy bg-white p-4 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <span>
            {total} diagnostic lead{total === 1 ? '' : 's'}
          </span>

          <div className="flex items-center gap-3">
            <button
              type="button"
              className="btn-secondary px-3 py-1.5"
              disabled={currentPage === 1}
              onClick={() =>
                setCurrentPage((page) => page - 1)
              }
            >
              Previous
            </button>

            <span>
              {currentPage} / {totalPages}
            </span>

            <button
              type="button"
              className="btn-secondary px-3 py-1.5"
              disabled={currentPage >= totalPages}
              onClick={() =>
                setCurrentPage((page) => page + 1)
              }
            >
              Next
            </button>
          </div>
        </footer>
      )}
    </div>
  );
}

function DiagnosticLeadRow({
  lead,
}: {
  lead: DiagnosticLead;
}) {
  const reportUrl = lead.reportPublicToken
    ? `/report/${lead.reportPublicToken}`
    : null;

  return (
    <tr className="transition-colors hover:bg-slate-50">
      <td className="px-5 py-4">
        <div className="font-semibold text-slate-800">
          {lead.name || '-'}
        </div>
      </td>

      <td className="px-5 py-4">
        {lead.email ? (
          <a
            href={`mailto:${lead.email}`}
            className="text-blue-600 hover:underline"
          >
            {lead.email}
          </a>
        ) : (
          <span className="text-slate-400">-</span>
        )}
      </td>

      <td className="px-5 py-4 text-slate-600">
        {lead.company || '-'}
      </td>

      <td className="max-w-64 px-5 py-4">
        {lead.websiteUrl ? (
          <a
            href={lead.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            {lead.websiteUrl}
          </a>
        ) : (
          <span className="text-slate-400">-</span>
        )}
      </td>

      <td className="px-5 py-4">
        <span
          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
            TYPE_CLASSES[lead.diagnosticType] ??
            'bg-slate-100 text-slate-600'
          }`}
        >
          {lead.diagnosticType}
        </span>
      </td>

      <td className="whitespace-nowrap px-5 py-4 text-slate-600">
        {new Date(lead.createdDateUtc).toLocaleString()}
      </td>

      {/* <td className="px-5 py-4">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
            lead.isEmailSent
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-amber-50 text-amber-700'
          }`}
        >
          <Mail size={13} />

          {lead.isEmailSent ? 'Sent' : 'Pending'}
        </span>
      </td> */}

      <td className="px-5 py-4">
        {reportUrl ? (
          <a
            href={reportUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline inline-flex items-center gap-1"
          >
            <ExternalLink size={15} />
            View
          </a>
        ) : (
          <span className="text-slate-400">
            Unavailable
          </span>
        )}
      </td>
    </tr>
  );
}